import { FileText, ListTodo, Paperclip, Plus } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Pressable, StyleSheet, View, Text, Platform } from "react-native";

interface AddToMessageProps {
  onAddAttachment?: () => void;
  onAddTask?: () => void;
  onAddAssignment?: () => void;
}

export const AddToMessage = ({ onAddAttachment, onAddAssignment, onAddTask }: AddToMessageProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMenuBtnHovered, setIsMenuBtnHovered] = useState(false);

  const [hoveredOption, setHoveredOption] = useState<number | null>(null);
  const menuRef = useRef(null);
  const isWeb = Platform.OS === "web";

  useEffect(() => {
    if (!isWeb) {
      return;
    }

    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
        setIsMenuBtnHovered(false);
        setHoveredOption(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isWeb]);

  return (
    <View style={styles.container} ref={menuRef}>
      <Pressable
        style={[styles.openMenuBtn, (isMenuBtnHovered || isMenuOpen) && styles.activeMenuBtn]}
        onMouseEnter={() => setIsMenuBtnHovered(true)}
        onMouseLeave={() => setIsMenuBtnHovered(false)}
        onPress={() => {
          setIsMenuOpen((prev) => !prev);
        }}>
        <Plus size={20} />
      </Pressable>
      {isMenuOpen && (
        <View style={styles.menuList}>
          {onAddAttachment && (
            <Pressable
              style={[styles.menuItem, hoveredOption === 0 && styles.activeMenuItem]}
              onMouseEnter={() => setHoveredOption(0)}
              onMouseLeave={() => setHoveredOption(null)}
              onPress={() => {
                onAddAttachment();
                setIsMenuOpen(false);
              }}>
              <Paperclip size={16} className="mr-2" />
              <Text numberOfLines={1}>Add attachment</Text>
            </Pressable>
          )}
          {onAddTask && (
            <Pressable
              style={[styles.menuItem, hoveredOption === 1 && styles.activeMenuItem]}
              onMouseEnter={() => setHoveredOption(1)}
              onMouseLeave={() => setHoveredOption(null)}
              onPress={() => {
                onAddTask();
                setIsMenuOpen(false);
              }}>
              <ListTodo size={16} className="mr-2" />
              <Text numberOfLines={1}>Add task</Text>
            </Pressable>
          )}
          {onAddAssignment && (
            <Pressable
              style={[styles.menuItem, hoveredOption === 2 && styles.activeMenuItem]}
              onMouseEnter={() => setHoveredOption(2)}
              onMouseLeave={() => setHoveredOption(null)}
              onPress={() => {
                onAddAssignment();
                setIsMenuOpen(false);
              }}>
              <FileText size={16} className="mr-2" />
              <Text numberOfLines={1}>Add assignment</Text>
            </Pressable>
          )}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: "relative",
    width: 36,
    height: 36,
  },
  openMenuBtn: {
    padding: 8,
    borderRadius: 99,
    aspectRatio: 1,
    color: "#667085",
  },
  activeMenuBtn: {
    backgroundColor: "#f2f4f7",
    color: "#101828",
  },
  menuList: {
    position: "absolute",
    marginBottom: 8,
    bottom: "100%",
    left: 0,
    backgroundColor: "#fff",
    borderRadius: 8,
    padding: 4,
    borderWidth: 1,
    borderColor: "#eaecf0",
    shadowColor: "#101828",
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 5,
  },
  menuItem: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    padding: 8,
    paddingRight: 32,
    gap: 8,
    borderRadius: 6,
    color: "#101828",
  },
  activeMenuItem: {
    backgroundColor: "#f2f4f7",
  },
});
